import { useMemo } from 'react';
import { Key, Edit, Trash2, CheckCircle2, Ban, Lock } from 'lucide-react';
import { formatUtcToJst } from '../../../utils/date';
import type { UserMasterInfo } from '../../../types';
import type { AppUser } from '../../../data/menu';
import { UserPermission, UserPermissionLabels } from '../../../types/UserPermission';
import { UserRole } from '../../../types/UserRole';

// マスタ名称解決用のインポート
import { useMasterReferenceQuery } from '../../../hooks/useMasterReferenceGetItemListQuery';
import { MasterCategoryConst } from '../../../types/masterSearchTypes';

/**
 * プロパティの型定義
 */
type Props = {
  data: UserMasterInfo[];                           // ユーザー一覧データ
  isLoading: boolean;                               // ローディング状態フラグ
  currentUser: AppUser | null;                      // 現在ログイン中のユーザー情報
  currentRole: UserRole;                            // 現在の役割
  onEdit: (user: UserMasterInfo) => void;           // 編集ボタンクリック時のハンドラー
  onResetPassword: (user: UserMasterInfo) => void;  // パスワード初期化ボタンクリック時のハンドラー
  onDelete: (user: UserMasterInfo) => void;         // 削除ボタンクリック時のハンドラー
};

/**
 * ユーザ管理データテーブル コンポーネント
 * ユーザ情報の表示と、各ユーザに対する操作（編集・初期化・削除）を提供
 */
export const UserMaintenanceDataTable = ({ 
  data, 
  isLoading, 
  currentUser, 
  currentRole,
  onEdit, 
  onResetPassword, 
  onDelete 
}: Props) => {

  // --- 0. マスタ名称解決用データの取得 ---
  // 現在のタブ役割に関わらず、Farmerなどの場合はSeller情報も必要なため一括で取得を管理
  const { data: sellerMaster } = useMasterReferenceQuery(true, MasterCategoryConst.SELLER);
  const { data: buyerMaster } = useMasterReferenceQuery(true, MasterCategoryConst.BUYER);
  const { data: farmerMaster } = useMasterReferenceQuery(true, MasterCategoryConst.FARMER);

  /**
   * コードから名称を検索するヘルパー関数
   * 引数の code は 0 埋め文字列が渡される想定
   */
  const getMasterName = (code: string, type: 'seller' | 'buyer' | 'farmer', parentSellerCode?: string) => {
    if (!code) return '';
    
    // 比較用に数値に変換
    const targetCode = Number(code);
    if (isNaN(targetCode)) return '';

    if (type === 'seller' && sellerMaster?.masters) {
      return sellerMaster.masters.find((m: any) => Number(m.sellerCode) === targetCode)?.sellerName || '';
    }
    if (type === 'buyer' && buyerMaster?.masters) {
      return buyerMaster.masters.find((m: any) => Number(m.buyerCode) === targetCode)?.buyerName || '';
    }
    if (type === 'farmer' && farmerMaster?.masters && parentSellerCode) {
      // 生産者は売手コードとの複合キーで検索
      const targetParent = Number(parentSellerCode);
      if (isNaN(targetParent)) return '';

      return farmerMaster.masters.find((m: any) => 
        Number(m.sellerCode) === targetParent && Number(m.farmerCode) === targetCode
      )?.farmerName || '';
    }
    return '';
  };

  // --- 1. 並び順（ソート）制御 ---
  /**
   * 役割に応じた並び替えを適用したデータ
   */
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      // マスターコードの分解
      const [aSeller, aFarmer] = (a.masterCode || '').split('_');
      const [bSeller, bFarmer] = (b.masterCode || '').split('_');

      if (currentRole === UserRole.Seller || currentRole === UserRole.Buyer) {
        // 【売手・買手】コード(masterCode) -> ログインID
        const codeComp = (a.masterCode || '').localeCompare(b.masterCode || '');
        if (codeComp !== 0) return codeComp;
        return (a.loginId || '').localeCompare(b.loginId || '');
      } 
      
      if (currentRole === UserRole.Farmer) {
        // 【生産者】売手コード -> 生産者コード -> ログインID
        const sellerComp = (aSeller || '').localeCompare(bSeller || '');
        if (sellerComp !== 0) return sellerComp;
        
        const farmerComp = (aFarmer || '').localeCompare(bFarmer || '');
        if (farmerComp !== 0) return farmerComp;
        
        return (a.loginId || '').localeCompare(b.loginId || '');
      }

      // 【市場・その他】ログインID順
      return (a.loginId || '').localeCompare(b.loginId || '');
    });
  }, [data, currentRole]);

  // --- 2. 定数・表示制御の設定 ---
  /** * テーブルに5行分を表示するために必要な最小高さの目安
   * ヘッダ(40px) + ボディ(52px * 5) + フッタ(40px) + 枠線等 ≒ 350px
   */
  const TABLE_MIN_H_5_ROWS = '350px';
  
  /** 検索フォームやヘッダーなど、テーブル外の要素が占有する高さの概算 */
  const EXTERNAL_H = '300px'; 
  
  /** データ件数が5件以上あるかどうかの判定フラグ */
  const hasMinRows = sortedData.length >= 5;

  // --- 3. ローディング表示 ---
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48 bg-white rounded-lg border border-gray-200">
        <div className="text-gray-500 font-bold">データを読み込み中...</div>
      </div>
    );
  }

  // --- 4. メインレンダリング ---
  return (
    /**
     * 高さ制御のロジック:
     * 1. minHeight: 5行以上ある場合は350pxを死守。これにより、狭い画面では親要素がスクロールされる。
     * 2. maxHeight: 画面が高い時は内部スクロール、低い時は外部スクロールへ切り替えるため max() 数式を適用。
     */
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden"
      style={{
        minHeight: hasMinRows ? TABLE_MIN_H_5_ROWS : 'auto',
        maxHeight: hasMinRows 
          ? `max(${TABLE_MIN_H_5_ROWS}, calc(90vh - ${EXTERNAL_H}))` 
          : 'none',
      }}
    >
      {/* overflow-auto により、コンテナの maxHeight を超えた場合に内部スクロールを発生させる */}
      <div className="overflow-auto flex-1 no-scrollbar">
        <table className="min-w-full divide-y divide-gray-200 border-separate border-spacing-0">
          <thead className="bg-gray-50">
            <tr>
              {/* sticky top-0 によりスクロール時も見出しを固定 */}
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200 w-24">状態</th>
              
              {/* --- 役割別の動的追加見出し --- */}
              {(currentRole === UserRole.Seller || currentRole === UserRole.Farmer) && (
                <>
                  <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">売手コード</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">売手名</th>
                </>
              )}
              {currentRole === UserRole.Farmer && (
                <>
                  <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">生産者コード</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">生産者名</th>
                </>
              )}
              {currentRole === UserRole.Buyer && (
                <>
                  <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">買手コード</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">買手名</th>
                </>
              )}

              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">ログインID</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">氏名</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">権限</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-sm font-bold text-gray-500 border-b border-gray-200">最終ログイン</th>
              <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-center text-sm font-bold text-gray-500 border-b border-gray-200 w-32">操作</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((row) => {
              // 自分自身のアカウントかどうかの判定（削除・パスワード初期化の制限用）
              const isMe: boolean = !!(currentUser?.Id && row.loginId && String(currentUser.Id) === String(row.loginId));
              // ユーザーが削除（無効化）済みかどうかの判定
              const isDisabledUser = !!row.isDeleted;
              // ユーザーがロック状態かどうか判定
              const isLockedUser = !!row.isLocked;
              // 操作実行ユーザーが管理者権限を持っているか
              const isAdmin: boolean = currentUser?.permission == UserPermission.Editor || currentUser?.permission == UserPermission.Admin;

              // 編集・削除等の操作が可能かどうかのガード（自身、無効ユーザー、非管理者を制限）
              const canEdit: boolean = !isMe && !isDisabledUser && isAdmin;

              // --- マスターコードのパースロジック ---
              // rawSCode等は名称検索用(0埋め維持)、displaySCode等は表示用(0埋め解除)
              let rawSCode = '', rawFCode = '', rawBCode = '';
              const mCode = row.masterCode || '';

              if (currentRole === UserRole.Seller) {
                rawSCode = mCode;
              } else if (currentRole === UserRole.Farmer) {
                const parts = mCode.split('_');
                rawSCode = parts[0] || '';
                rawFCode = parts[1] || '';
              } else if (currentRole === UserRole.Buyer) {
                rawBCode = mCode;
              }

              // 表示用にパディングを除去（数値化して文字列に戻す）
              const displaySCode = rawSCode ? String(Number(rawSCode)) : '';
              const displayFCode = rawFCode ? String(Number(rawFCode)) : '';
              const displayBCode = rawBCode ? String(Number(rawBCode)) : '';

              return (
                <tr key={row.id} className={`hover:bg-blue-50 transition-colors`}>
                  {/* 状態カラム：有効・ロック・無効をバッジ形式で表示 */}
                  <td className="px-6 py-4 whitespace-nowrap text-base">
                  {isDisabledUser ? (
                      /* 1. 無効 */
                      <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                        <Ban size={16} className="mr-1" />
                        <span className="text-sm font-bold">無効</span>
                      </div>
                    ) : isLockedUser ? (
                      /* 2. ロック */
                      <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-50 text-orange-400 border border-orange-200">
                        <Lock size={16} className="mr-1" />
                        <span className="text-sm font-bold">ロック</span>
                      </div>
                    ) : (
                      /* 3. 有効 */
                      <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={16} className="mr-1" />
                        <span className="text-sm font-bold">有効</span>
                      </div>
                    )}
                  </td>

                  {/* --- 役割に応じた動的データ表示（状態とログインIDの間） --- */}
                  {(currentRole === UserRole.Seller || currentRole === UserRole.Farmer) && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{displaySCode}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 truncate max-w-[150px]" title={getMasterName(rawSCode, 'seller')}>
                        {getMasterName(rawSCode, 'seller')}
                      </td>
                    </>
                  )}
                  {currentRole === UserRole.Farmer && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{displayFCode}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 truncate max-w-[150px]" title={getMasterName(rawFCode, 'farmer', rawSCode)}>
                        {getMasterName(rawFCode, 'farmer', rawSCode)}
                      </td>
                    </>
                  )}
                  {currentRole === UserRole.Buyer && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{displayBCode}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 truncate max-w-[150px]" title={getMasterName(rawBCode, 'buyer')}>
                        {getMasterName(rawBCode, 'buyer')}
                      </td>
                    </>
                  )}

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                    {row.loginId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-bold">{row.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className={`px-2 py-1 rounded text-sm border inline-block text-center ${
                      (row.permission === UserPermission.Editor || row.permission === UserPermission.Admin)
                        ? 'bg-red-50 text-red-600 border-red-200 font-bold' 
                        : 'bg-slate-100 text-gray-600 border-slate-200'
                    }`}
                    style={{ minWidth: '70px' }}
                    >
                      {UserPermissionLabels[row.permission]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {row.lastLoginAt ? formatUtcToJst(row.lastLoginAt).substring(0, 19) : ''}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <div className="flex justify-center gap-1.5">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canEdit) onResetPassword(row);
                        }} 
                        disabled={!canEdit}
                        className={`p-1.5 rounded transition-colors ${
                          (!canEdit)
                            ? 'text-gray-300 cursor-not-allowed opacity-40' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="パスワード初期化"
                      >
                        <Key size={24}/>
                      </button>
                      
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isMe && isAdmin) onEdit(row);
                        }} 
                        disabled={!(!isMe && isAdmin)}
                        className={`p-1.5 rounded transition-colors ${
                          (!(!isMe && isAdmin))
                            ? 'text-gray-300 cursor-not-allowed opacity-40' 
                            : 'text-blue-600 hover:bg-red-100'
                        }`}
                        title="編集"
                      >
                        <Edit size={24}/>
                      </button>
                      
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canEdit) onDelete(row);
                        }} 
                        disabled={!canEdit}
                        className={`p-1.5 rounded transition-colors ${
                          (!canEdit)
                            ? 'text-gray-300 cursor-not-allowed opacity-40' 
                            : 'text-red-500 hover:bg-red-100'
                        }`}
                        title="無効化"
                      >
                        <Trash2 size={20}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* フッターエリア：件数表示（非固定設定により全体のスクロール末尾に配置） */}
      <div className="flex-none bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center">
        <span className="font-medium">全 {data.length} 件を表示</span>
      </div>
    </div>
  );
};